import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { UserService } from '../user/user.service';
import { environment } from 'src/environments/environment';
import { PrintSettingService } from '../settings/hospital-setting/print-setting/print-setting.service';
import { lastValueFrom } from 'rxjs';

@Injectable()
export class AppointmentPrintService {

  private readonly defaultLogo = 'assets/images/svgs/hospital.png';

  constructor(
    private http: HttpClient,
    private datePipe: DatePipe,
    private userService: UserService,
    private hospitalState: HospitalStateService,
    private printSettingService: PrintSettingService
  ) { }

  async printSchedule(appointments: any[], selectedDate: Date, doctorId: number, showPatientInfo: boolean) {
    const content = await this.buildPrintContent(appointments, selectedDate, doctorId, showPatientInfo);
    this.openPrintWindow(content);
  }

  private async buildPrintContent(appointments: any[], selectedDate: Date, doctorId: number, showPatientInfo: boolean): Promise<string> {
    let template = (await lastValueFrom(this.http
      .get('assets/unified-print-template.html', { responseType: 'text' }))) || '';

    const hospital = this.hospitalState.getHospital() || {};
    const settings = await this.printSettingService.getPrintSettingPromise(hospital.id);
    const doctor = await lastValueFrom(this.userService.getUserByID(doctorId));
    const doctorName = `${doctor?.result?.firstName ?? ''} ${doctor?.result?.lastName ?? ''}`.trim();

    if (!showPatientInfo) {
      template = template.replace('</head>', `<style>#showPatientInfo { display: none !important; }</style></head>`);
    }

    const tableHtml = this.buildTableHtml(appointments, doctorName, selectedDate);
    let finalHtml = this.applyPlaceholders(template, tableHtml, doctorName, doctor, hospital);

    // Apply settings
    if (settings) {
      const logoPos = settings.logoPosition === 'center' ? 'column' : (settings.logoPosition === 'left' ? 'row' : 'row-reverse');
      const headerClass = settings.logoPosition === 'center' ? 'center' : '';
      const headerAlignment = settings.addressAlignment || 'left';

      const styles = `
                :root {
                    --logo-size: ${settings.logoSize || '60px'};
                    --logo-position: ${logoPos};
                    --header-alignment: ${headerAlignment};
                    --hospital-name-size: ${settings.hospitalNameSize || 20}px;
                    --address-font-size: ${settings.addressFontSize || 13}px;
                    --footer-display: ${settings.showFooter ? 'block' : 'none'};
                    --doctor-signature-top-display: ${settings.showFooter && settings.doctorNamePosition === 'top' ? 'block' : 'none'};
                    --doctor-signature-bottom-display: ${settings.showFooter && settings.doctorNamePosition === 'footer' ? 'block' : 'none'};
                }
            `;
      finalHtml = finalHtml.replace('</head>', `<style>${styles}</style></head>`).replace('{{headerClass}}', headerClass);
    } else {
      finalHtml = finalHtml.replace('{{headerClass}}', '');
    }

    return finalHtml;
  }

  private buildTableHtml(appointments: any[], doctorName: string, selectedDate: Date): string {
    const rows = this.buildRows(appointments);
    const totalCharge = this.calculateTotalCharge(appointments);
    const formattedDate = this.datePipe.transform(selectedDate, 'dd-MM-yyyy') || '';

    return `
    <h2 style="text-align:center; margin-bottom:6px;">Appointment Schedule</h2>
    <strong>Doctor Name:</strong> Dr. ${doctorName} <br/>
    <strong>Date:</strong> ${formattedDate} <br/>
    <div class="report-table" style="display:flex; flex-direction:column; margin-top:6px; border:1px solid #aaa; border-radius:6px; overflow:hidden;">
      <div class="header" style="display:flex; text-align:center; font-weight:600; background:#f2f2f2; border-bottom:1px solid #ccc;">
        <div style="flex:1;">Time</div>
        <div style="flex:2;">Name</div>
        <div style="flex:2;">Appointment Type</div>
        <div style="flex:1;">OPD No</div>
        <div style="flex:1;">Status</div>
        <div style="flex:1;">Charge</div>
      </div>
      ${rows}
    </div>
    <div style="text-align:right; font-weight:600; margin-top:6px;">Total Charge: ${totalCharge}</div>`;
  }

  private buildRows(appointments: any[]): string {
    return appointments.map(app => `
      <div class="row" style="display:flex; text-align:center; border-bottom:1px solid #ccc; padding:4px 0;">
        <div class="cell" style="flex:1;">${app.time || ''}</div>
        <div class="cell" style="flex:2;">${app.appointment?.patientName || ''}</div>
        <div class="cell" style="flex:2;">${app.appointment?.appointmentTypeName || ''}</div>
        <div class="cell" style="flex:1;">${app.appointment?.opdNumber || ''}</div>
        <div class="cell" style="flex:1;">${app.appointment?.status || ''}</div>
        <div class="cell" style="flex:1;">${app.appointment?.charge || ''}</div>
      </div>`).join('');
  }

  private calculateTotalCharge(appointments: any[]): number {
    return appointments.reduce((sum: number, app: any) => {
      const value = Number(app.appointment?.charge);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
  }

  private applyPlaceholders(template: string, tableHtml: string, doctorName: string, doctor: any, hospital: any): string {
    const fullImagePath = hospital.logo ? `${environment.imagePath}${hospital.logo}` : this.defaultLogo;

    const replacements: Record<string, string> = {
      reportTitle: 'Appointment Schedule',
      hospitalName: hospital.name || '',
      hospitalAddress: (hospital.address || '') + (hospital.city ? ' ,' + hospital.city : ''),
      phone: hospital.phone || '',
      regNo: hospital.registrationNumber ? `Registration No: ${hospital.registrationNumber}` : '',
      reportSections: tableHtml,
      doctorName: doctorName,
      qualification: doctor?.result?.degree || '',
      email: doctor?.result?.email || '',
      logo: `<img src="${fullImagePath}" class="logo" onerror="this.onerror=null;this.src='${this.defaultLogo}'"/>`
    };

    let filledTemplate = template;
    for (const key in replacements) {
      const value = replacements[key] ?? '';
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
      filledTemplate = filledTemplate.replace(regex, value);
    }
    return filledTemplate;
  }

  private openPrintWindow(content: string): void {

    let iframe = document.getElementById('print-frame') as HTMLIFrameElement;

    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'print-frame';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    const doc = iframe.contentWindow?.document;

    if (!doc) return;

    doc.open();
    doc.write(content);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.print();
    }, 300);
  }
}
